import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Chip, Stack, Tooltip, Typography, useTheme} from '@mui/material';
import ForceGraph2D from 'react-force-graph-2d';
import TabEmpty from './TabEmpty';
import TabLoading from './TabLoading';

// ─── constants ────────────────────────────────────────────────────────────────

const NODE_RADIUS = 7;
const ENTITY_RADIUS = 22;

// 4 document cluster types + the center entity
const CLUSTER_TYPES = ['fintel', 'draft', 'dataItem', 'corpus'];

// Each node type maps to its own cluster region
const NODE_CLUSTER = {
    fintel:   'fintel',
    draft:    'draft',
    dataItem: 'dataItem',
    corpus:   'corpus',
};

const NODE_COLORS = {
    entity:   '#1565c0',  // primary blue
    fintel:   '#2e7d32',  // green — approved FINTEL
    draft:    '#e65100',  // orange — draft reports
    dataItem: '#6a1b9a',  // purple — data items
    corpus:   '#37474f',  // dark grey-blue — corpus
};

// Faint background tint for each cluster zone (drawn behind nodes)
const CLUSTER_COLORS = {
    fintel:   '#2e7d3214',
    draft:    '#e6510014',
    dataItem: '#6a1b9a14',
    corpus:   '#37474f14',
};

const CLUSTER_BORDER_COLORS = {
    fintel:   '#2e7d3240',
    draft:    '#e6510040',
    dataItem: '#6a1b9a40',
    corpus:   '#37474f40',
};

// Legend entries
const LEGEND_ITEMS = [
    {type: 'fintel',   label: 'FINTEL',    cluster: 'fintel'},
    {type: 'draft',    label: 'Draft',     cluster: 'draft'},
    {type: 'dataItem', label: 'Files', cluster: 'dataItem'},
    {type: 'corpus',   label: 'Corpus',    cluster: 'corpus'},
];

// Cluster center offsets (relative to canvas centre 0,0) — 4-corner arrangement
const CLUSTER_CENTERS = {
    fintel:   {x: -300, y: -210},  // upper-left
    draft:    {x:  300, y: -210},  // upper-right
    dataItem: {x:  300, y:  210},  // lower-right
    corpus:   {x: -300, y:  210},  // lower-left
};

// ─── graph data builder ───────────────────────────────────────────────────────

function buildAllGraphData({entity, fintelClaims, draftClaims, mentions, dataItems, fintelReportHeadersById, draftReportHeadersById}) {
    const nodes = [];
    const links = [];

    nodes.push({
        id: 'entity',
        label: entity?.canonicalName || 'Entity',
        type: 'entity',
        cluster: 'entity',
        count: null,
        navTarget: null,
    });

    // FINTEL (approved) — deduplicate by reportId
    const fintelByReport = new Map();
    for (const claim of fintelClaims) {
        const rId = claim.reportId || claim.report_id;
        if (!rId) continue;
        fintelByReport.set(rId, {count: (fintelByReport.get(rId)?.count || 0) + 1});
    }
    for (const [reportId, {count}] of fintelByReport) {
        const header = fintelReportHeadersById?.[reportId];
        const label = header?.title || header?.reportTitle || `Report ${reportId.slice(0, 8)}`;
        const id = `fintel-${reportId}`;
        nodes.push({id, label, type: 'fintel', cluster: 'fintel', count, navTarget: `/products/${reportId}`});
        links.push({source: 'entity', target: id, count});
    }

    // Draft reports — deduplicate by reportId; skip any already emitted as FINTEL
    const draftByReport = new Map();
    for (const claim of draftClaims) {
        const rId = claim.reportId || claim.report_id;
        if (!rId || fintelByReport.has(rId)) continue;
        draftByReport.set(rId, {count: (draftByReport.get(rId)?.count || 0) + 1});
    }
    for (const [reportId, {count}] of draftByReport) {
        const header = draftReportHeadersById?.[reportId];
        const label = header?.title || header?.reportTitle || `Draft ${reportId.slice(0, 8)}`;
        const id = `draft-${reportId}`;
        nodes.push({id, label, type: 'draft', cluster: 'draft', count, navTarget: `/products/${reportId}`});
        links.push({source: 'entity', target: id, count});
    }

    // Data Items — deduplicate by dataItemId, keep highest mentionCount
    const dataItemsById = new Map();
    for (const item of dataItems) {
        const itemId = item.dataItemId ?? item.id;
        if (itemId == null) continue;
        const existing = dataItemsById.get(itemId);
        if (!existing || (item.mentionCount ?? 1) > (existing.mentionCount ?? 1)) {
            dataItemsById.set(itemId, item);
        }
    }
    for (const [itemId, item] of dataItemsById) {
        const id = `dataitem-${itemId}`;
        nodes.push({
            id, label: item.title || `Source ${itemId}`,
            type: 'dataItem', cluster: 'dataItem',
            count: item.mentionCount ?? 1,
            navTarget: null,
        });
        links.push({source: 'entity', target: id, count: item.mentionCount ?? 1});
    }

    // Corpus — deduplicate by docId, keep highest mentionCount
    const mentionsByDocId = new Map();
    for (const mention of mentions) {
        const docId = mention.docId;
        if (docId == null) continue;
        const existing = mentionsByDocId.get(docId);
        if (!existing || (mention.mentionCount ?? 1) > (existing.mentionCount ?? 1)) {
            mentionsByDocId.set(docId, mention);
        }
    }
    for (const [docId, mention] of mentionsByDocId) {
        const id = `corpus-${docId}`;
        const docSrc = mention.docSource || '';
        const hasFullUrl = docSrc.startsWith('http');
        // Bare domain names (e.g. "atlanticcouncil.org") become navigable https:// links.
        const isDomain = !hasFullUrl && /^[a-z0-9]+([\-.][a-z0-9]+)*\.[a-z]{2,}$/i.test(docSrc.trim());

        // Only use a linked report as nav target if the user can actually access it.
        const linkedReport = mention.reports?.[0];
        const linkedReportAccessible = linkedReport?.reportId &&
            (fintelReportHeadersById?.[linkedReport.reportId] || draftReportHeadersById?.[linkedReport.reportId]);

        let navTarget = null;
        let navExternal = false;
        if (mention.docUrl) {
            // Direct article URL — most specific, always prefer this
            navTarget = mention.docUrl;
            navExternal = true;
        } else if (hasFullUrl) {
            navTarget = docSrc;
            navExternal = true;
        } else if (isDomain) {
            navTarget = `https://${docSrc}`;
            navExternal = true;
        } else if (linkedReportAccessible) {
            navTarget = `/products/${linkedReport.reportId}/view`;
        }

        nodes.push({
            id, label: mention.docTitle || mention.docSource || `Doc ${docId}`,
            type: 'corpus', cluster: 'corpus',
            count: mention.mentionCount ?? 1,
            navTarget,
            navExternal,
        });
        links.push({source: 'entity', target: id, count: mention.mentionCount ?? 1});
    }

    return {nodes, links};
}

// ─── filter + top-N trimming ──────────────────────────────────────────────────

const TOP_N_OPTIONS = [10, 25, 50, 100, Infinity]; // Infinity = All

/**
 * 1. Multi-select type filter (empty set = show all types)
 * 2. Top-N per cluster by mention count (sorted desc)
 * Returns {nodes, links, clusterTotals} where clusterTotals shows full size before trim.
 */
function filterAndTrimGraphData(allData, activeFilters, topN) {
    // Step 1 — type filter
    let nodes = allData.nodes;
    let links = allData.links;

    if (activeFilters.size > 0) {
        const allowed = new Set(['entity']);
        for (const n of nodes) {
            if (activeFilters.has(n.type)) allowed.add(n.id);
        }
        nodes = nodes.filter(n => allowed.has(n.id));
        links = links.filter(l => {
            const s = typeof l.source === 'object' ? l.source.id : l.source;
            const t = typeof l.target === 'object' ? l.target.id : l.target;
            return allowed.has(s) && allowed.has(t);
        });
    }

    // Step 2 — count totals per cluster BEFORE trimming
    const clusterTotals = {};
    for (const n of nodes) {
        if (n.cluster !== 'entity') {
            clusterTotals[n.cluster] = (clusterTotals[n.cluster] || 0) + 1;
        }
    }

    // Step 3 — top-N per cluster (sorted by count desc)
    if (topN !== Infinity) {
        const allowed = new Set(['entity']);
        for (const cluster of CLUSTER_TYPES) {
            nodes
                .filter(n => n.cluster === cluster)
                .sort((a, b) => (b.count || 0) - (a.count || 0))
                .slice(0, topN)
                .forEach(n => allowed.add(n.id));
        }
        nodes = nodes.filter(n => allowed.has(n.id));
        links = links.filter(l => {
            const s = typeof l.source === 'object' ? l.source.id : l.source;
            const t = typeof l.target === 'object' ? l.target.id : l.target;
            return allowed.has(s) && allowed.has(t);
        });
    }

    return {nodes, links, clusterTotals};
}

// ─── canvas draw helpers ──────────────────────────────────────────────────────

// Wrap text into lines that fit within maxWidth pixels at current scale.
function wrapLabel(ctx, text, maxWidth) {
    const words = text.split(' ');
    const lines = [];
    let current = '';
    for (const word of words) {
        const test = current ? `${current} ${word}` : word;
        if (current && ctx.measureText(test).width > maxWidth) {
            lines.push(current);
            current = word;
        } else {
            current = test;
        }
    }
    if (current) lines.push(current);
    return lines;
}

function drawClusterBackground(ctx, nodes, width, height) {
    for (const cluster of CLUSTER_TYPES) {
        const clusterNodes = nodes.filter(n => n.cluster === cluster && n.x != null && n.y != null);
        if (clusterNodes.length === 0) continue;

        // Compute bounding box of cluster nodes
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        for (const n of clusterNodes) {
            minX = Math.min(minX, n.x);
            maxX = Math.max(maxX, n.x);
            minY = Math.min(minY, n.y);
            maxY = Math.max(maxY, n.y);
        }
        const pad = 55;
        const cx = (minX + maxX) / 2;
        const cy = (minY + maxY) / 2;
        const rx = Math.max((maxX - minX) / 2 + pad, 50);
        const ry = Math.max((maxY - minY) / 2 + pad, 50);

        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, 2 * Math.PI);
        ctx.fillStyle = CLUSTER_COLORS[cluster];
        ctx.fill();
        ctx.strokeStyle = CLUSTER_BORDER_COLORS[cluster];
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.stroke();
        ctx.setLineDash([]);
    }
}

function drawNode(node, ctx, globalScale, hoveredId, avatarImg) {
    const isEntity = node.type === 'entity';
    const r = isEntity ? ENTITY_RADIUS : NODE_RADIUS;
    const color = NODE_COLORS[node.type] || '#90a4ae';
    const isHovered = node.id === hoveredId;

    if (isHovered) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, r + 5, 0, 2 * Math.PI);
        ctx.fillStyle = color + '33';
        ctx.fill();
    }

    // Filled circle background
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();

    // Entity avatar image clipped to circle
    if (isEntity && avatarImg) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(node.x, node.y, r - 1, 0, 2 * Math.PI);
        ctx.clip();
        ctx.drawImage(avatarImg, node.x - r + 1, node.y - r + 1, (r - 1) * 2, (r - 1) * 2);
        ctx.restore();
    }

    // Border ring
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    if (isEntity) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2.5 / globalScale;
        ctx.stroke();
    } else if (isHovered) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2 / globalScale;
        ctx.stroke();
    } else if (node.navTarget) {
        // Subtle ring to signal the node is clickable
        ctx.strokeStyle = 'rgba(255,255,255,0.5)';
        ctx.lineWidth = 1.2 / globalScale;
        ctx.stroke();
    }

    const fontSize = isEntity ? 13 / globalScale : 11 / globalScale;
    // Always draw labels — wrap long names across multiple lines
    ctx.font = `${isEntity ? 700 : 500} ${fontSize}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const maxLabelWidth = (isEntity ? 160 : 130) / globalScale;
    const lineHeight = (fontSize + 2 / globalScale);
    const lines = wrapLabel(ctx, node.label, maxLabelWidth);

    const totalHeight = lines.length * lineHeight;
    const startY = node.y + r + fontSize * 0.8;

    lines.forEach((line, i) => {
        const ly = startY + i * lineHeight;
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fillText(line, node.x + 0.5, ly + 0.5);
        ctx.fillStyle = isEntity ? '#0d47a1' : '#1a1a1a';
        ctx.fillText(line, node.x, ly);
    });
}

function drawLink(link, ctx, globalScale) {
    const s = link.source;
    const t = link.target;
    if (!s || !t || typeof s === 'string' || typeof t === 'string') return;

    ctx.beginPath();
    ctx.moveTo(s.x, s.y);
    ctx.lineTo(t.x, t.y);
    ctx.strokeStyle = 'rgba(0,0,0,0.15)';
    ctx.lineWidth = Math.max(0.8, Math.min(link.count / 3, 4)) / globalScale;
    ctx.stroke();

    if (globalScale > 0.6) {
        const mx = (s.x + t.x) / 2;
        const my = (s.y + t.y) / 2;
        const fontSize = 9 / globalScale;
        const txt = `${link.count} mention${link.count !== 1 ? 's' : ''}`;
        ctx.font = `${fontSize}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.fillText(txt, mx + 0.5, my + 0.5);
        ctx.fillStyle = '#546e7a';
        ctx.fillText(txt, mx, my);
    }
}

// ─── main component ───────────────────────────────────────────────────────────

const GraphTab = ({
    entity,
    fintelClaims,
    draftClaims,
    fintelReportHeadersById,
    draftReportHeadersById,
    mentions,
    dataItems,
    loading,
}) => {
    const theme = useTheme();
    // Callback ref so the ResizeObserver attaches the instant the container Box
    // enters the DOM — even if that happens after the initial mount (e.g. while
    // the loading spinner is shown). Using useRef + useEffect([]) would miss this
    // because the empty-dep effect fires once before the Box is rendered.
    const [containerEl, setContainerEl] = useState(null);
    const containerRef = useCallback((el) => setContainerEl(el), []);
    const graphRef = useRef(null);
    const avatarImgRef = useRef(null);
    const [dimensions, setDimensions] = useState({width: 800, height: 700});
    const [hoveredNodeId, setHoveredNodeId] = useState(null);
    const [activeFilters, setActiveFilters] = useState(new Set());
    const [topN, setTopN] = useState(10);
    const [avatarReady, setAvatarReady] = useState(false);

    // Pre-load entity avatar image for canvas drawing.
    // No crossOrigin — the entity.image/imageUrl is a same-origin /api/entities/.../avatar path
    // served from our S3 proxy, so no CORS issue. After load, reheat the d3 simulation
    // to force a canvas redraw cycle so the avatar actually appears.
    const entityImageSrc = entity?.image || entity?.imageUrl || null;
    useEffect(() => {
        setAvatarReady(false);
        avatarImgRef.current = null;
        if (!entityImageSrc) return;
        const img = new Image();
        img.onload = () => {
            avatarImgRef.current = img;
            setAvatarReady(true);
            // Simulation may have cooled — kick it to redraw with the new image
            graphRef.current?.d3ReheatSimulation();
        };
        img.onerror = () => {
            avatarImgRef.current = null;
        };
        img.src = entityImageSrc;
    }, [entityImageSrc]);

    // Responsive sizing — fires whenever containerEl changes (i.e. when the Box
    // mounts or unmounts). Reads initial size immediately and tracks resizes.
    useEffect(() => {
        if (!containerEl) return;
        const read = () => {
            const r = containerEl.getBoundingClientRect();
            if (r.width > 0) setDimensions({width: r.width, height: Math.max(500, r.height)});
        };
        read(); // immediate read
        const obs = new ResizeObserver(() => read());
        obs.observe(containerEl);
        return () => obs.disconnect();
    }, [containerEl]);

    const allGraphData = useMemo(() => {
        if (!entity) return {nodes: [], links: []};
        return buildAllGraphData({entity, fintelClaims, draftClaims, mentions, dataItems, fintelReportHeadersById, draftReportHeadersById});
    }, [entity, fintelClaims, draftClaims, mentions, dataItems, fintelReportHeadersById, draftReportHeadersById]);

    const {nodes: graphNodes, links: graphLinks, clusterTotals} = useMemo(
        () => filterAndTrimGraphData(allGraphData, activeFilters, topN),
        [allGraphData, activeFilters, topN]
    );
    const graphData = useMemo(() => ({nodes: graphNodes, links: graphLinks}), [graphNodes, graphLinks]);

    // Apply cluster force whenever graph data changes
    useEffect(() => {
        const fg = graphRef.current;
        if (!fg) return;

        fg.d3Force('cluster', (alpha) => {
            for (const node of graphData.nodes) {
                const center = CLUSTER_CENTERS[node.cluster];
                if (!center) continue;
                node.vx = (node.vx || 0) - (node.x - center.x) * alpha * 0.18;
                node.vy = (node.vy || 0) - (node.y - center.y) * alpha * 0.18;
            }
        });

        // Weaker link force so cluster force dominates
        fg.d3Force('link')?.strength(0.08);
        // Pull entity node firmly to centre
        fg.d3Force('center')?.strength(0);
        fg.d3Force('charge')?.strength(-200);
    }, [graphData]);

    // Keep entity node pinned at canvas centre
    useEffect(() => {
        const entityNode = graphData.nodes.find(n => n.id === 'entity');
        if (entityNode) {
            entityNode.fx = 0;
            entityNode.fy = 0;
        }
    }, [graphData]);

    // Re-centre view whenever graph data OR canvas dimensions change
    useEffect(() => {
        if (!graphRef.current || graphData.nodes.length === 0) return;
        const timer = setTimeout(() => {
            graphRef.current?.zoomToFit(400, 40);
        }, 400);
        return () => clearTimeout(timer);
    }, [graphData, dimensions]);

    const handleLegendClick = useCallback((type) => {
        setActiveFilters(prev => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type); else next.add(type);
            return next;
        });
    }, []);

    const handleNodeClick = useCallback((node) => {
        if (!node.navTarget) return;
        // Always open in a new tab — keeps the Involvement graph in place.
        // The report viewer at /products/:id/view enforces its own auth checks;
        // users without access will see the normal access-denied page.
        window.open(node.navTarget, '_blank', 'noopener,noreferrer');
    }, []);

    const handleNodeHover = useCallback((node) => {
        setHoveredNodeId(node ? node.id : null);
        if (containerEl) {
            containerEl.style.cursor = node?.navTarget ? 'pointer' : 'default';
        }
    }, [containerEl]);

    const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
        drawNode(node, ctx, globalScale, hoveredNodeId, avatarImgRef.current);
    }, [hoveredNodeId, avatarReady]); // eslint-disable-line react-hooks/exhaustive-deps

    const linkCanvasObject = useCallback((link, ctx, globalScale) => {
        drawLink(link, ctx, globalScale);
    }, []);

    // Draw cluster backgrounds before nodes (onRenderFramePre)
    const onRenderFramePre = useCallback((ctx) => {
        drawClusterBackground(ctx, graphData.nodes, dimensions.width, dimensions.height);
    }, [graphData.nodes, dimensions]);

    if (loading) return <TabLoading/>;

    const totalDocNodes = allGraphData.nodes.length - 1;
    if (totalDocNodes === 0) {
        return <TabEmpty message="This entity has no mentions across any documents yet."/>;
    }

    // Count per type from ALL data (for legend badges)
    const countByType = {};
    for (const n of allGraphData.nodes) {
        if (n.type !== 'entity') countByType[n.type] = (countByType[n.type] || 0) + 1;
    }
    const visibleItems = LEGEND_ITEMS.filter(l => (countByType[l.type] || 0) > 0);

    // Visible count per type after trim
    const visibleCountByType = {};
    for (const n of graphNodes) {
        if (n.type !== 'entity') visibleCountByType[n.type] = (visibleCountByType[n.type] || 0) + 1;
    }

    return (
        <Stack>
            {/* Legend + Top-N bar */}
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{px: 2, pt: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider'}}
            >
                <Typography variant="caption" color="text.secondary" sx={{mr: 0.5, flexShrink: 0}}>
                    Filter:
                </Typography>
                {visibleItems.map(({type, label}) => {
                    const total = countByType[type] || 0;
                    const shown = visibleCountByType[type] || 0;
                    const isSelected = activeFilters.has(type);
                    const isDimmed = activeFilters.size > 0 && !isSelected;
                    const isTrimmed = topN !== Infinity && total > topN;
                    const chipLabel = isTrimmed
                        ? `${label} (${shown} of ${total})`
                        : `${label} (${total})`;
                    return (
                        <Tooltip
                            key={type}
                            title={isTrimmed ? `Showing top ${shown} of ${total} by mention count` : ''}
                            placement="top"
                        >
                            <Chip
                                label={chipLabel}
                                size="small"
                                onClick={() => handleLegendClick(type)}
                                sx={{
                                    bgcolor: isDimmed ? 'action.disabledBackground' : NODE_COLORS[type],
                                    color: isDimmed ? 'text.disabled' : '#fff',
                                    fontWeight: isSelected ? 700 : 500,
                                    fontSize: 11,
                                    height: 22,
                                    cursor: 'pointer',
                                    outline: isSelected ? `2px solid ${NODE_COLORS[type]}` : 'none',
                                    outlineOffset: 1,
                                    transition: 'all 0.15s',
                                    '&:hover': {opacity: 0.85},
                                }}
                            />
                        </Tooltip>
                    );
                })}
                {activeFilters.size > 0 && (
                    <Chip
                        label="Clear"
                        size="small"
                        variant="outlined"
                        onClick={() => setActiveFilters(new Set())}
                        sx={{fontSize: 11, height: 22, cursor: 'pointer'}}
                    />
                )}

                {/* Top-N selector */}
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ml: 'auto !important', flexShrink: 0}}>
                    <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>Top:</Typography>
                    {TOP_N_OPTIONS.map(n => (
                        <Chip
                            key={n}
                            label={n === Infinity ? 'All' : n}
                            size="small"
                            onClick={() => setTopN(n)}
                            sx={{
                                fontSize: 11,
                                height: 20,
                                cursor: 'pointer',
                                bgcolor: topN === n ? 'primary.main' : 'action.hover',
                                color: topN === n ? '#fff' : 'text.primary',
                                fontWeight: topN === n ? 700 : 400,
                                '&:hover': {opacity: 0.8},
                            }}
                        />
                    ))}
                </Stack>

                <Tooltip title="Click filter chips to isolate types (multi-select). Top-N shows highest-mention nodes per cluster. Click a document node to open it.">
                    <Typography variant="caption" color="text.disabled" sx={{cursor: 'help', flexShrink: 0}}>?</Typography>
                </Tooltip>
            </Stack>

            {/* Cluster labels overlay */}
            <Box sx={{position: 'relative'}}>
                <Box sx={{
                    position: 'absolute', top: 8, left: 8, zIndex: 1,
                    display: 'flex', flexDirection: 'column', gap: 0.5, pointerEvents: 'none',
                }}>
                    {[
                        {cluster: 'fintel',   label: 'FINTEL (approved)', color: NODE_COLORS.fintel},
                        {cluster: 'draft',    label: 'Draft',             color: NODE_COLORS.draft},
                        {cluster: 'dataItem', label: 'Files',        color: NODE_COLORS.dataItem},
                        {cluster: 'corpus',   label: 'Corpus',            color: NODE_COLORS.corpus},
                    ].filter(c => allGraphData.nodes.some(n => n.cluster === c.cluster)).map(({cluster, label, color}) => {
                        const total = clusterTotals?.[cluster] || 0;
                        const shown = graphNodes.filter(n => n.cluster === cluster).length;
                        const trimmed = topN !== Infinity && total > topN;
                        return (
                        <Box key={cluster} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0}}/>
                            <Typography variant="caption" sx={{color, fontWeight: 600, fontSize: 10, opacity: 0.85}}>
                                {label}{trimmed ? ` · top ${shown} of ${total}` : total > 0 ? ` · ${total}` : ''}
                            </Typography>
                        </Box>
                        );
                    })}
                </Box>

                {/* Graph canvas */}
                <Box
                    ref={containerRef}
                    sx={{
                        width: '100%',
                        height: 700,
                        bgcolor: theme.palette.mode === 'dark' ? '#12121f' : '#f5f6f8',
                        overflow: 'hidden',
                    }}
                >
                    <ForceGraph2D
                        ref={graphRef}
                        graphData={graphData}
                        width={dimensions.width}
                        height={dimensions.height}
                        nodeCanvasObject={nodeCanvasObject}
                        nodeCanvasObjectMode={() => 'replace'}
                        linkCanvasObject={linkCanvasObject}
                        linkCanvasObjectMode={() => 'replace'}
                        onRenderFramePre={onRenderFramePre}
                        onNodeClick={handleNodeClick}
                        onNodeHover={handleNodeHover}
                        nodePointerAreaPaint={(node, color, ctx) => {
                            const r = node.type === 'entity' ? ENTITY_RADIUS : NODE_RADIUS;
                            ctx.fillStyle = color;
                            ctx.beginPath();
                            ctx.arc(node.x, node.y, r + 4, 0, 2 * Math.PI);
                            ctx.fill();
                        }}
                        cooldownTicks={200}
                        d3AlphaDecay={0.018}
                        d3VelocityDecay={0.35}
                        onEngineStop={() => graphRef.current?.zoomToFit(400, 40)}
                        enableZoomInteraction
                        enablePanInteraction
                        minZoom={0.15}
                        maxZoom={8}
                        backgroundColor={theme.palette.mode === 'dark' ? '#12121f' : '#f5f6f8'}
                    />
                </Box>
            </Box>
        </Stack>
    );
};

export default GraphTab;
